import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { OutlookSyncTab, OutlookSyncType, SyncHistory } from '../models';

export const OUTLOOK_SYNC_FEATURE_KEY = 'outlook-sync-configuration';

export interface SaveResult {
  type: 'success' | 'error';
  message: string;
}

export interface OutlookSyncState {
  saving: boolean;
  lastResult: SaveResult | null;
  autoHealAttempted: boolean;
  reauthSucceeded: boolean;
  configLoadFailed: boolean;
  selectedTab: OutlookSyncTab | null;
  lastHistoryByType: Partial<Record<OutlookSyncType, SyncHistory[]>>;
}

// Legacy key preserves user's last-selected tab across deploys; rename = data loss.
const SELECTED_TAB_STORAGE_KEY = 'outlookSync.selectedTab';
const VALID_TABS: readonly OutlookSyncTab[] = [
  'emails',
  'contacts',
  'appointments',
  'tasks',
];

function readStoredSelectedTab(): OutlookSyncTab | null {
  try {
    const raw = sessionStorage.getItem(SELECTED_TAB_STORAGE_KEY);
    if (raw && (VALID_TABS as readonly string[]).includes(raw))
      return raw as OutlookSyncTab;
  } catch {
    /* sessionStorage unavailable */
  }
  return null;
}

function writeStoredSelectedTab(tab: OutlookSyncTab): void {
  try {
    sessionStorage.setItem(SELECTED_TAB_STORAGE_KEY, tab);
  } catch {
    /* sessionStorage unavailable */
  }
}

export const initialOutlookSyncState: OutlookSyncState = {
  saving: false,
  lastResult: null,
  autoHealAttempted: false,
  reauthSucceeded: false,
  configLoadFailed: false,
  selectedTab: readStoredSelectedTab(),
  lastHistoryByType: {},
};

export const OutlookSyncStore = signalStore(
  { providedIn: 'root' },
  withDevtools(OUTLOOK_SYNC_FEATURE_KEY),
  withState(initialOutlookSyncState),
  withMethods((store) => ({
    beginSave(): void {
      patchState(store, { saving: true, lastResult: null });
    },
    reportSuccess(message: string): void {
      patchState(store, {
        saving: false,
        lastResult: { type: 'success', message },
      });
    },
    reportError(message: string): void {
      patchState(store, {
        saving: false,
        lastResult: { type: 'error', message },
      });
    },
    markAutoHealAttempted(): void {
      patchState(store, { autoHealAttempted: true });
    },
    resetAutoHealAttempted(): void {
      patchState(store, { autoHealAttempted: false });
    },
    reportReauthSuccess(): void {
      patchState(store, { reauthSucceeded: true });
    },
    reportConfigLoadFailure(): void {
      patchState(store, { configLoadFailed: true });
    },
    reportConfigLoadSuccess(): void {
      patchState(store, { configLoadFailed: false });
    },
    reportAuthFailure(): void {
      patchState(store, { configLoadFailed: true });
    },
    setSelectedTab(tab: OutlookSyncTab): void {
      if (store.selectedTab() !== tab) {
        patchState(store, { selectedTab: tab });
        writeStoredSelectedTab(tab);
      }
    },
    ensureSelectedTab(tab: OutlookSyncTab): void {
      if (store.selectedTab() === null) {
        patchState(store, { selectedTab: tab });
        writeStoredSelectedTab(tab);
      }
    },
    setLastHistory(type: OutlookSyncType, history: SyncHistory[]): void {
      patchState(store, (state) => ({
        lastHistoryByType: { ...state.lastHistoryByType, [type]: history },
      }));
    },
  })),
);

export type OutlookSyncStore = InstanceType<typeof OutlookSyncStore>;
